<template>
    <div class="row search-container">
        <div class="gap" />
        <form action="#" @submit.prevent="search">
            <div class="col-xs-12 col-md-6 relative">
                <small v-if="!$device.mobile && badge" class="badge badge-arrow badge--red badge-bundle">{{ $t('noLimitation') }}</small>
                <div class="input-pair">
                    <package-origin-picker v-model="value.origin"
                                           tabindex="1"
                                           ref="origin"
                                           :placeholder="$t('common.origin')"
                    />
                    <package-destination-picker ref="destination"
                                                tabindex="2"
                                                v-model="value.destination"
                                                :placeholder="$t('common.destination')"
                                                :is-destination="true"
                                                :origin-id="value.origin && value.origin.originId"
                    />
                </div>
            </div>
            <div class="col-xs-12 col-md-2 search-date">
                <date-range-picker v-model="value.range"
                                   :origin-id="value.origin && value.origin.id"
                                   :destination-id="value.destination && value.destination.id"
                                   tabindex="3"
                                   :placeholder="$t('selectRange')"
                                   @set-selected-date="setSelectedDate"
                                   @set-tour-search-date="setTourSearchDate"
                                   ref="rangePicker"
                                   :show-input="false"
                >
                    <div slot="empty">
                        <div class="text-center py-2">
                            {{ $t('firstSelectOrigin') }}
                        </div>
                    </div>
                    <i slot="icon" class="icon icon-calendar" />
                </date-range-picker>
            </div>
            <div class="col-xs-12 col-md-2 search-date">
                <tour-passenger-picker v-model="rooms" :tabindex="4" ref="passengers" />
            </div>
            <div class="col-xs-12 col-md-2">
                <search-btn :disabled="disabled" :loading="disabled" tabindex="4" />
            </div>
            <div class="clearfix" />
        </form>
    </div>
</template>

<script>
// components
import DateRangePicker from '~/components/pages/tour/search/date-range-picker';
import SearchPanel from '~/components/common/search/search-panel';
import PackageOriginPicker from '~/components/pages/tour/search/package-origin-picker';
import PackageDestinationPicker from '~/components/pages/tour/search/package-destination-picker';
import TourPassengerPicker from '~/components/pages/tour/search/tour-passenger-picker';
import SearchBtn from '~/components/common/search/search-btn';
// utils
import { roomsInfo } from '~/components/pages/international-hotel/utils';

const DATE_FORMAT = 'jYYYY/jMM/jDD';

export default {
    components: {
        TourPassengerPicker,
        PackageOriginPicker,
        PackageDestinationPicker,
        SearchBtn,
        DateRangePicker,
    },
    extends: SearchPanel,

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },

        value: {
            type: Object,
            default: () => ({
                destination: null,
                rooms: [{
                    type: 'unspecified',
                    adults: [30, 30],
                    children: [],
                }],
                range: null,
            }),
        },

        badge: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            openPassengers: false,
            selectedDate: null,
            rooms: this.value.rooms ||
                [ // Submitted to available results
                    {
                        type: 'unspecified',
                        adults: [30, 30],
                        children: [],
                    },
                ],
        };
    },
    computed: {
        // Passenger input placeHolder
        passengersText () {
            const { adultCount, childCount, roomCount } = roomsInfo(this.rooms);

            return this.$t('passengersText.path', [adultCount, (childCount > 0 ? this.$t('passengersText.child1', [childCount]) : ''), (roomCount > 0 ? roomCount : 1)]);
        },
        datePickerValue: {
            get () {
                return this.value.range ? [
                    this.value.range.from ? this.$moment.utc(this.value.range.from, DATE_FORMAT) : null,
                    this.value.range.to ? this.$moment.utc(this.value.range.to, DATE_FORMAT) : null,
                ] : [null, null];
            },

            set (x) {
                this.value.range = {
                    ...(this.value.range || {}),
                    from: x[0] ? x[0].format(DATE_FORMAT) : null,
                };
                this.value.range = {
                    ...(this.value.range || {}),
                    to: x[1] ? x[1].format(DATE_FORMAT) : null,
                };
            },
        },
    },
    async mounted () {
        const { origin, destination, rangePicker, passengers } = this.$refs;
        this.autoNextInput([origin, destination, rangePicker, passengers]);
    },
    methods: {
        validate () {
            if (!this.value.destination || !this.value.destination.title) return this.$t('fillDestination');
            if (!this.value.origin || !this.value.origin.title) return this.$t('fillOrigin');
            if (!this.value.range || !this.value.range.from || !this.value.range.to) return this.$t('fillDate');
            return null;
        },

        search () {
            const error = this.validate();
            if (error) {
                this.$emit('error', error);
                return;
            }

            const data = {};

            data.origin = this.value.origin ? this.value.origin.id : null;
            data.destination = this.value.destination ? this.value.destination.id : null;
            data.from = this.value.range.from ? this.$moment(this.value.range.from).format('YYYY-MM-DD') : '';
            data.to = this.value.range.to ? this.$moment(this.value.range.to).format('YYYY-MM-DD') : '';
            data.rooms = JSON.stringify(this.rooms);
            data.sort = 'asc';
            data.isDateRange = 'true';
            // send search tracking event
            this.sendSearchTrackingEvent(`${data.origin || ''}-${data.destination || ''} ${data.from}-${data.to}`);

            this.$emit('create-new-search', data);
            this.$emit('set-selected-date', this.selectedDate);
        },

        scrollDown () {
            // Really, i had no choice. ($)
            window.scrollIt((document.querySelector('.city-container') || {}).offsetTop || 1100);
        },

        sendSearchTrackingEvent (label) {
            // temporarily disable search tracking events due to farid's requests
            if (label) return;
            // search tracking
            this.$gtm.sendGTMEvent({
                event: 'event',
                eventCategory: 'Tour',
                eventAction: 'Search',
                eventLabel: label,
            });
        },

        setSelectedDate (event) {
            this.selectedDate = event;
            this.$emit('set-selected-date', event);
        },

        setTourSearchDate (event) {
            this.selectedDate = event;
        },
    },
};
</script>

<style lang="scss">
@import '~vars';
@import '~assets/styles/animate';

.badge-bundle {
    position: absolute;
    top: -35px;
    border-radius: 5px;
    margin: 0;
    padding: 6px;
}

.search-date {

    .search-form-provider__inner {
        @media (min-width: 992px) {

            &.tour-search {
                height: 47px;
            }
        }
        @media (max-width: 767px) {

            &.tour-search {
                height: 49px;
            }
        }
        background: white;

        .search-departing-date {
            border-left: 1px solid $gray-light;
        }

        .search-returning-date {
            padding: 0;
        }

        .input-group {
            border: none;
        }
    }
}
</style>

<i18n src="~/lang/tour.json"></i18n>
<i18n>
{
    "fa": {
        "fillDestination": "لطفا مقصد مورد نظر خود را تعیین کنید.",
        "fillDate": "لطفا بازه سفر خود را تعیین کنید.",
        "fillOrigin": "لطفا مبدا مورد نظر خود را تعیین کنید.",
        "noLimitation": "بدون محدودیت در مبدا",
        "passengersText": {
            "path": "{0} بزرگسال، {1}{2} اتاق",
            "child1": "{0} کودک, "
        },
        "specialOfferOfWebsite": "پیشنهادات ویژه علی‌بابا",
        "selectRange": "زمان تور",
        "firstSelectOrigin": "ابتدا مبدا و مقصد را انتخاب کنید"
    }
}
</i18n>
